import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock, FaHeart, FaFacebook, FaInstagram, FaTwitter, FaWhatsapp } from "react-icons/fa";
import { useLanguage } from '../../context/LanguageContext';
import PageHeader from './PageHeader';

const ContactUsSection = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const t = {
    English: {
      pageHeaderTitle: "Contact Us",
      title: "Get in Touch With Us",
      subtitle: "We're Here to Help You Find Your Perfect Match",
      description: "Have questions or need assistance? Our dedicated support team is available 24/7 to guide you on your journey to finding everlasting love.",
      contactInfo: "Contact Information",
      phone: "Phone",
      phoneNumber: "+91 98765 43210",
      email: "Email",
      emailAddress: "info@softyetech.com",
      address: "Address",
      addressLine: "ST Match Zone Pvt Ltd",
      addressDetails: "573, Softye Technologies\nMettur main road, Ammapettai - Erode",
      supportHours: "Support Hours",
      supportTime: "24/7 Available – We never sleep when it comes to love!",
      formTitle: "Send Us a Message",
      namePlaceholder: "Your Full Name",
      emailPlaceholder: "Your Email Address",
      phonePlaceholder: "Your Phone Number (Optional)",
      messagePlaceholder: "How can we help you today?",
      sendButton: "Send Message",
      successMessage: "Thank you! We'll get back to you soon ❤️",
      socialTitle: "Follow Us for Love Stories & Updates"
    },
    Tamil: {
      pageHeaderTitle: "தொடர்பு கொள்ளுங்கள்",
      title: "எங்களை தொடர்பு கொள்ளுங்கள்",
      subtitle: "உங்கள் சரியான இணையைக் கண்டறிய நாங்கள் உதவ இங்கே உள்ளோம்",
      description: "கேள்விகள் உள்ளனவா அல்லது உதவி தேவையா? உங்கள் வாழ்நாள் காதல் பயணத்தில் உங்களுக்கு வழிகாட்ட எங்கள் அர்ப்பணிப்புள்ள ஆதரவுக் குழு 24/7 கிடைக்கிறது.",
      contactInfo: "தொடர்புத் தகவல்",
      phone: "தொலைபேசி",
      phoneNumber: "+91 98765 43210",
      email: "மின்னஞ்சல்",
      emailAddress: "info@softyetech.com",
      address: "முகவரி",
      addressLine: "ST Match Zone Pvt Ltd",
      addressDetails: "573, Softye Technologies\nMettur main road, Ammapettai - Erode",
      supportHours: "ஆதரவு நேரம்",
      supportTime: "24/7 கிடைக்கும் – காதல் விஷயத்தில் நாங்கள் ஒருபோதும் தூங்குவதில்லை!",
      formTitle: "எங்களுக்கு செய்தி அனுப்புங்கள்",
      namePlaceholder: "உங்கள் முழு பெயர்",
      emailPlaceholder: "உங்கள் மின்னஞ்சல் முகவரி",
      phonePlaceholder: "உங்கள் தொலைபேசி எண் (விரும்பினால்)",
      messagePlaceholder: "இன்று நாங்கள் உங்களுக்கு எப்படி உதவ முடியும்?",
      sendButton: "செய்தி அனுப்பு",
      successMessage: "நன்றி! விரைவில் உங்களுக்கு பதிலளிப்போம் ❤️",
      socialTitle: "காதல் கதைகள் & புதுப்பிப்புகளுக்கு எங்களை பின்தொடருங்கள்"
    }
  };

  const txt = t[language];

  // Simple form state (in real app, connect to backend or email service)
  const handleSubmit = (e) => {
    e.preventDefault();
    alert(txt.successMessage);
    e.target.reset();
  };

  return (
    <>
      {/* Reusable Page Header */}
      <PageHeader 
        showBackButton={true} 
        backTo="/" 
        title={txt.pageHeaderTitle}
      />

      <section className="relative py-16 md:py-20 lg:py-24 px-4 md:px-8 bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-hidden">
        {/* Subtle Background Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-rose-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          {/* Hero Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 md:mb-16"
          >
            <motion.div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-rose-100 text-rose-700 font-medium text-base md:text-lg uppercase tracking-wider mb-6">
              <FaHeart className="animate-pulse text-xl" />
              {language === 'English' ? 'We’re Here For You' : 'நாங்கள் உங்களுக்காக இங்கே உள்ளோம்'}
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
              {txt.title}
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl font-light text-rose-600 mb-6">
              {txt.subtitle}
            </p>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {txt.description}
            </p>
          </motion.div>

          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-rose-100 text-center"
            >
              <div className="inline-flex p-5 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-white text-3xl mb-4">
                <FaPhoneAlt />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{txt.phone}</h3>
              <p className="text-rose-600 font-semibold">{txt.phoneNumber}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-rose-100 text-center"
            >
              <div className="inline-flex p-5 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-white text-3xl mb-4">
                <FaEnvelope />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{txt.email}</h3>
              <p className="text-rose-600 font-semibold break-words">{txt.emailAddress}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-rose-100 text-center"
            >
              <div className="inline-flex p-5 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-white text-3xl mb-4">
                <FaMapMarkerAlt />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{txt.address}</h3>
              <p className="text-gray-600 text-sm whitespace-pre-line">{txt.addressDetails}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-rose-100 text-center"
            >
              <div className="inline-flex p-5 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-white text-3xl mb-4">
                <FaClock />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{txt.supportHours}</h3>
              <p className="text-gray-600 text-sm">{txt.supportTime}</p>
            </motion.div>
          </div>

          {/* Contact Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl border border-rose-100"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{txt.formTitle}</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  placeholder={txt.namePlaceholder}
                  required
                  className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-rose-500 focus:outline-none transition"
                />
                <input
                  type="email"
                  placeholder={txt.emailPlaceholder}
                  required
                  className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-rose-500 focus:outline-none transition"
                />
                <input
                  type="tel"
                  placeholder={txt.phonePlaceholder}
                  className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-rose-500 focus:outline-none transition"
                />
                <textarea
                  rows="5"
                  placeholder={txt.messagePlaceholder}
                  required
                  className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-rose-500 focus:outline-none transition resize-none"
                ></textarea>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-lg font-bold rounded-xl shadow-xl flex items-center justify-center gap-3 group"
                >
                  {txt.sendButton}
                  <FaHeart className="group-hover:scale-125 transition-transform" />
                </motion.button>
              </form>
            </motion.div>

            {/* Optional: Embed Google Map (uncomment if needed) */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-xl border border-rose-100"
            >
              <div className="bg-gray-200 border-2 border-dashed rounded-xl w-full h-full min-h-96 flex items-center justify-center text-gray-500">
                Google Map Placeholder<br />(Embed your map iframe here)
              </div>
            </motion.div>
          </div>

          {/* Social Media */}
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{txt.socialTitle}</h3>
            <div className="flex justify-center gap-6">
              <a href="/" className="p-4 bg-rose-100 rounded-full text-rose-600 hover:bg-rose-500 hover:text-white transition text-2xl">
                <FaFacebook />
              </a>
              <a href="/" className="p-4 bg-rose-100 rounded-full text-rose-600 hover:bg-rose-500 hover:text-white transition text-2xl">
                <FaInstagram />
              </a>
              <a href="/" className="p-4 bg-rose-100 rounded-full text-rose-600 hover:bg-rose-500 hover:text-white transition text-2xl">
                <FaTwitter />
              </a>
              <a href="/" className="p-4 bg-rose-100 rounded-full text-rose-600 hover:bg-rose-500 hover:text-white transition text-2xl">
                <FaWhatsapp />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactUsSection;